import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Edit2,
  Trash,
  Search,
  Calendar,
  Plus,
  ListChecks,
  Users,
  AlertTriangle,
  Pencil,
  User,
  Clock,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Tag, Select, DatePicker, Form, Tooltip } from "antd";
import dayjs from "dayjs";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import {
  SchemaService,
  DynamicModuleService,
  PtsProjectService,
  DropdownService,
} from "@/services/APIs";
import { get, post } from "@/services/axiosUitils";
import LoadingSpinner from "@/components/common/LoadingSpinner";

const SPRINT_SCHEMA_NAME = "PTS Sprint";
const PTS_TASK_SCHEMA_NAME = "PTS Task";
// Task status field holds PTS Status (priority) id; use same dropdown as Task page
const PTS_STATUS_SCHEMA_NAME = "PTS Status";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";

// Fetch and render employee name for edited tooltip.
const EmployeeTooltipContent = ({
  userId,
}: {
  userId: string | null | undefined;
}) => {
  const { data: employeeData, isLoading } = useQuery({
    queryKey: ["employee-by-user-id", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      try {
        const modelName = encodeURIComponent(EMPLOYEE_SCHEMA_NAME);
        return await get<any>(`/formily/dynamic-model/${modelName}/`, {
          params: { users__eq: userId },
        });
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });

  const employeeName = useMemo(() => {
    if (!employeeData) {
      return "Loading...";
    }
    const results =
      employeeData?.results ??
      employeeData?.data ??
      (Array.isArray(employeeData) ? employeeData : []);
    if (results.length === 0) {
      return "Unknown Employee";
    }
    const employee = results[0];
    const firstName =
      employee.first_name ??
      employee.firstname ??
      employee.fname ??
      employee.firstName ??
      "";
    const lastName =
      employee.last_name ??
      employee.lastname ??
      employee.lname ??
      employee.lastName ??
      "";
    return `${firstName} ${lastName}`.trim() || "Unknown Employee";
  }, [employeeData]);

  if (isLoading) {
    return <span className="text-gray-700">Loading...</span>;
  }
  return <span className="text-gray-700">{employeeName}</span>;
};

/** Employee-task-remaining API response (week-wise or legacy flat) */
interface EmployeeTaskRemainingWeekBlock {
  week_label: string;
  week_start: string;
  week_end: string;
  remaining_hours: Array<{ date: string; hours: number; is_leave?: boolean }>;
}
interface EmployeeTaskRemainingResult {
  task_id: string;
  emp_id: string;
  emp_name: string;
  week_wise?: EmployeeTaskRemainingWeekBlock[];
  /** Legacy: flat list per employee */
  remaining_hours?: Array<{ date: string; hours: number; is_leave?: boolean }>;
}
interface EmployeeTaskRemainingResponse {
  count: number;
  results: EmployeeTaskRemainingResult[];
  /** Populated when normalizing edit API: emp_id -> date -> allocation_hours (seed inputs) */
  __editSeedHours?: Record<string, Record<string, number>>;
}

/** Raw response from GET employee-sprint-task-hours (edit sprint) */
interface EmployeeSprintTaskHoursDay {
  date: string;
  remaining_hours: number;
  allocation_hours: number;
  is_leave?: boolean;
}
interface EmployeeSprintTaskHoursWeek {
  week_label: string;
  week_start: string;
  week_end: string;
  dates: EmployeeSprintTaskHoursDay[];
}
interface EmployeeSprintTaskHoursEmp {
  emp_id: string;
  emp_name: string;
  allocation_data: EmployeeSprintTaskHoursWeek[];
}
interface EmployeeSprintTaskHoursResponse {
  sprint_id: string;
  task_id: string;
  sprint_start_date?: string;
  sprint_end_date?: string;
  count: number;
  next: null;
  previous: null;
  results: EmployeeSprintTaskHoursEmp[];
}

/** Map edit API shape -> same structure as employee-task-remaining for grids + save payload */
function normalizeSprintTaskHoursResponse(
  raw: EmployeeSprintTaskHoursResponse | null | undefined,
  taskId: string
): EmployeeTaskRemainingResponse {
  if (!raw?.results?.length) {
    return { count: raw?.count ?? 0, results: [] };
  }
  const __editSeedHours: Record<string, Record<string, number>> = {};
  const results: EmployeeTaskRemainingResult[] = raw.results.map(emp => {
    const seedForEmp: Record<string, number> = {};
    const week_wise: EmployeeTaskRemainingWeekBlock[] = (
      emp.allocation_data ?? []
    ).map(week => ({
      week_label: week.week_label,
      week_start: week.week_start,
      week_end: week.week_end,
      remaining_hours: (week.dates ?? []).map(d => {
        seedForEmp[d.date] = Number(d.allocation_hours ?? 0);
        return {
          date: d.date,
          hours:
            Number(d.remaining_hours ?? 0) + Number(d.allocation_hours ?? 0),
          is_leave: Boolean(d.is_leave),
        };
      }),
    }));
    __editSeedHours[String(emp.emp_id)] = seedForEmp;
    return {
      task_id: String(raw.task_id ?? taskId),
      emp_id: String(emp.emp_id),
      emp_name: emp.emp_name,
      week_wise,
    };
  });
  return {
    count: raw.count,
    results,
    __editSeedHours,
  };
}

interface EmployeeDayAvailability {
  remainingHours?: number;
  isLeave: boolean;
}

/** Resolve API remaining hours for one employee + date (week_wise or legacy) */
function getEmployeeDayAvailability(
  empResult: EmployeeTaskRemainingResult | undefined,
  date: string
): EmployeeDayAvailability {
  if (!empResult) {
    return { remainingHours: undefined, isLeave: false };
  }
  if (empResult.week_wise?.length) {
    for (const w of empResult.week_wise) {
      const found = w.remaining_hours?.find(h => h.date === date);
      if (found !== undefined) {
        return {
          remainingHours: found.hours,
          isLeave: Boolean(found.is_leave),
        };
      }
    }
    return { remainingHours: undefined, isLeave: false };
  }
  const found = empResult.remaining_hours?.find(h => h.date === date);
  return {
    remainingHours: found?.hours,
    isLeave: Boolean(found?.is_leave),
  };
}

function getRemainingHoursForEmployee(
  empResult: EmployeeTaskRemainingResult | undefined,
  date: string
): number | undefined {
  return getEmployeeDayAvailability(empResult, date).remainingHours;
}

/** Working days between start and end (inclusive), returns { date: YYYY-MM-DD, label: "Mon 09" } */
function getWorkingDays(
  startDate: dayjs.Dayjs | null | undefined,
  endDate: dayjs.Dayjs | null | undefined
): { date: string; label: string }[] {
  if (!startDate || !endDate || endDate.isBefore(startDate)) {
    return [];
  }
  const days: { date: string; label: string }[] = [];
  let d = startDate.startOf("day");
  const end = endDate.startOf("day");
  while (!d.isAfter(end)) {
    const dateStr = d.format("YYYY-MM-DD");
    const label = d.format("ddd DD");
    days.push({ date: dateStr, label });
    d = d.add(1, "day");
  }
  return days;
}

/**
 * Input default = 0. If user edited a cell, use that value.
 */
function getEffectiveAllocatedHours(
  byDate: Record<string, number>,
  date: string,
  _apiHours: number | undefined
): { effective: number; hasUserSet: boolean } {
  const hasUserSet = Object.prototype.hasOwnProperty.call(byDate, date);
  if (hasUserSet) {
    return { effective: byDate[date], hasUserSet: true };
  }
  return { effective: 0, hasUserSet: false };
}

/** Allocation inputs: ignore decimals — only whole hours (e.g. 2.7 → 2). */
function parseWholeAllocatedHours(raw: string): number {
  if (raw.trim() === "" || raw === "-") {
    return 0;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.floor(n));
}

/** Task estimated hours can come from multiple backend field names. */
function getTaskEstimatedHours(task: any): number {
  const raw =
    task?.estimatedhours ??
    task?.taskestimatedhours ??
    task?.est_hours ??
    task?.estimated_hours ??
    0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Day-wise allocation grid: Employee column + date columns (input + "Xh left") + Total */
function DayWiseEmployeeGrid({
  employees,
  workingDays,
  dayHoursMap,
  onUpdateHours,
  getRemainingHours,
  getDayAvailability,
  readOnly = false,
}: {
  employees: { id: string; name: string }[];
  workingDays: { date: string; label: string }[];
  dayHoursMap: Record<string, Record<string, number>>;
  onUpdateHours: (empId: string, date: string, hours: number) => void;
  getRemainingHours: (empId: string, date: string) => number | undefined;
  getDayAvailability: (empId: string, date: string) => EmployeeDayAvailability;
  readOnly?: boolean;
}) {
  const getTotalForEmp = (empId: string) => {
    const byDate = dayHoursMap[empId] || {};
    return workingDays.reduce((sum, { date }) => {
      const { isLeave } = getDayAvailability(empId, date);
      if (isLeave) {
        return sum;
      }
      const api = getRemainingHours(empId, date);
      const { effective } = getEffectiveAllocatedHours(byDate, date, api);
      return sum + effective;
    }, 0);
  };

  return (
    <div className="min-w-0 max-w-full overflow-x-auto rounded-md border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="sticky left-0 z-30 min-w-[140px] border-r border-gray-200 bg-gray-50 font-semibold text-gray-700 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
              Employee
            </TableHead>
            {workingDays.map(({ date, label }) => (
              <TableHead
                key={date}
                className="min-w-[90px] text-center font-semibold text-gray-700"
              >
                {label}
              </TableHead>
            ))}
            <TableHead className="min-w-[60px] text-right font-semibold text-gray-700">
              Total
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.map(emp => {
            const byDate = dayHoursMap[emp.id] || {};
            const total = getTotalForEmp(emp.id);
            return (
              <TableRow key={emp.id} className="bg-white">
                <TableCell className="sticky left-0 z-20 min-w-[140px] border-r border-gray-200 bg-white font-medium text-gray-900 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-600">
                      <Users className="h-4 w-4" />
                    </div>
                    {emp.name}
                  </div>
                </TableCell>
                {workingDays.map(({ date }) => {
                  const { remainingHours: baseRemaining, isLeave } =
                    getDayAvailability(emp.id, date);
                  const { effective } = getEffectiveAllocatedHours(
                    byDate,
                    date,
                    baseRemaining
                  );
                  const leftAfterAlloc =
                    baseRemaining !== undefined
                      ? baseRemaining - effective
                      : undefined;
                  const isOverAllocated =
                    leftAfterAlloc !== undefined && leftAfterAlloc < 0;
                  const formatLeft = (n: number) =>
                    Number.isInteger(n) ? String(n) : n.toFixed(1);
                  const inputValue = effective;

                  return (
                    <TableCell key={date} className="text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        {isLeave ? (
                          <div className="flex h-8 w-full items-center justify-center rounded-lg border border-red-200 bg-red-50 text-center text-xs font-semibold text-red-600">
                            LEAVE
                          </div>
                        ) : readOnly ? (
                          <div
                            className={`flex h-8 w-full items-center justify-center rounded-lg border text-center text-sm ${
                              isOverAllocated
                                ? "border-red-300 bg-red-50/50"
                                : "bg-muted/30 border-gray-200"
                            }`}
                          >
                            {inputValue}
                          </div>
                        ) : (
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            value={inputValue}
                            onChange={e => {
                              const raw = e.target.value;
                              onUpdateHours(
                                emp.id,
                                date,
                                parseWholeAllocatedHours(raw)
                              );
                            }}
                            className={`h-8 w-full rounded-lg text-center text-sm ${
                              isOverAllocated
                                ? "border-red-400 focus-visible:ring-red-200"
                                : "border-gray-200"
                            }`}
                          />
                        )}
                        {!isLeave && leftAfterAlloc !== undefined && (
                          <div
                            className={`flex items-center justify-center gap-0.5 text-[10px] ${
                              isOverAllocated
                                ? "font-medium text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {isOverAllocated && (
                              <AlertTriangle
                                className="h-3 w-3 shrink-0"
                                aria-hidden
                              />
                            )}
                            <span>
                              {formatLeft(leftAfterAlloc)}h{" "}
                              {isOverAllocated ? "Overload" : "left"}
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  );
                })}
                <TableCell className="text-right font-semibold text-gray-900">
                  {total}h
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** "Week 1 (Mon 23 – Fri 27)" style header from API week block */
function formatWeekGroupHeader(week: EmployeeTaskRemainingWeekBlock): string {
  const days = week.remaining_hours ?? [];
  if (days.length === 0) {
    return week.week_label;
  }
  const first = dayjs(days[0].date).format("ddd DD");
  const last = dayjs(days[days.length - 1].date).format("ddd DD");
  return `${week.week_label} (${first} – ${last})`;
}

/**
 * Single table with two-level header: week groups spanning day columns, then day labels + Total (matches design).
 */
function WeekGroupedEmployeeGrid({
  weekWiseBlocks,
  employees,
  dayHoursMap,
  onUpdateHours,
  getRemainingHours,
  getDayAvailability,
  readOnly = false,
}: {
  weekWiseBlocks: EmployeeTaskRemainingWeekBlock[];
  employees: { id: string; name: string }[];
  dayHoursMap: Record<string, Record<string, number>>;
  onUpdateHours: (empId: string, date: string, hours: number) => void;
  getRemainingHours: (empId: string, date: string) => number | undefined;
  getDayAvailability: (empId: string, date: string) => EmployeeDayAvailability;
  readOnly?: boolean;
}) {
  const flatDays = useMemo(() => {
    const out: { date: string; label: string; weekStartKey: string }[] = [];
    for (const week of weekWiseBlocks) {
      const wkKey = `${week.week_start}-${week.week_end}`;
      for (const rh of week.remaining_hours ?? []) {
        out.push({
          date: rh.date,
          label: dayjs(rh.date).format("ddd DD"),
          weekStartKey: wkKey,
        });
      }
    }
    return out;
  }, [weekWiseBlocks]);

  const getTotalForEmp = (empId: string) => {
    const byDate = dayHoursMap[empId] || {};
    return flatDays.reduce((sum, { date }) => {
      const { isLeave } = getDayAvailability(empId, date);
      if (isLeave) {
        return sum;
      }
      const api = getRemainingHours(empId, date);
      const { effective } = getEffectiveAllocatedHours(byDate, date, api);
      return sum + effective;
    }, 0);
  };

  const renderDayCell = (empId: string, date: string, isWeekFirst: boolean) => {
    const byDate = dayHoursMap[empId] || {};
    const { remainingHours: baseRemaining, isLeave } = getDayAvailability(
      empId,
      date
    );
    const { effective } = getEffectiveAllocatedHours(
      byDate,
      date,
      baseRemaining
    );
    const leftAfterAlloc =
      baseRemaining !== undefined ? baseRemaining - effective : undefined;
    const isOverAllocated = leftAfterAlloc !== undefined && leftAfterAlloc < 0;
    const formatLeft = (n: number) =>
      Number.isInteger(n) ? String(n) : n.toFixed(1);
    const inputValue = effective;

    return (
      <TableCell
        key={date}
        className={`text-center ${isWeekFirst ? "border-l-2 border-l-gray-300" : ""}`}
      >
        <div className="flex flex-col items-center gap-0.5">
          {isLeave ? (
            <div className="flex h-8 w-full items-center justify-center rounded-lg border border-red-200 bg-red-50 text-center text-xs font-semibold text-red-600">
              LEAVE
            </div>
          ) : readOnly ? (
            <div
              className={`flex h-8 w-full items-center justify-center rounded-lg border text-center text-sm ${
                isOverAllocated
                  ? "border-red-300 bg-red-50/50"
                  : "bg-muted/30 border-gray-200"
              }`}
            >
              {inputValue}
            </div>
          ) : (
            <Input
              type="number"
              min={0}
              step={1}
              value={inputValue}
              onChange={e => {
                const raw = e.target.value;
                onUpdateHours(empId, date, parseWholeAllocatedHours(raw));
              }}
              className={`h-8 w-full rounded-lg text-center text-sm ${
                isOverAllocated
                  ? "border-red-400 focus-visible:ring-red-200"
                  : "border-gray-200"
              }`}
            />
          )}
          {!isLeave && leftAfterAlloc !== undefined && (
            <div
              className={`flex items-center justify-center gap-0.5 text-[10px] ${
                isOverAllocated ? "font-medium text-red-600" : "text-gray-500"
              }`}
            >
              {isOverAllocated && (
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
              )}
              <span>
                {formatLeft(leftAfterAlloc)}h{" "}
                {isOverAllocated ? "Overload" : "left"}
              </span>
            </div>
          )}
        </div>
      </TableCell>
    );
  };

  if (flatDays.length === 0) {
    return null;
  }

  return (
    <div className="min-w-0 max-w-full overflow-x-auto rounded-md border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          {/* Row 1: week bands only (Employee / Total align on row 2 with day headers) */}
          <TableRow className="bg-gray-100">
            <TableHead
              className="sticky left-0 z-30 min-w-[140px] border-b border-r border-gray-200 bg-gray-100 p-2 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]"
              aria-hidden
            />
            {weekWiseBlocks.map((week, idx) => {
              const n = week.remaining_hours?.length ?? 0;
              if (n === 0) {
                return null;
              }
              return (
                <TableHead
                  key={`${week.week_start}-${week.week_end}`}
                  colSpan={n}
                  className={`border-b border-gray-200 bg-gray-100 px-2 py-2 text-center text-xs font-semibold text-gray-800 ${
                    idx > 0 ? "border-l-2 border-l-gray-300" : ""
                  }`}
                >
                  {formatWeekGroupHeader(week)}
                </TableHead>
              );
            })}
            <TableHead
              className="min-w-[60px] border-b border-gray-200 bg-gray-100 p-2"
              aria-hidden
            />
          </TableRow>
          {/* Row 2: Employee | Mon 23 … | Total */}
          <TableRow className="bg-gray-50">
            <TableHead className="sticky left-0 z-30 min-w-[140px] border-b border-r border-gray-200 bg-gray-50 px-2 py-2 text-left text-xs font-bold text-gray-800 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
              Employee
            </TableHead>
            {flatDays.map((d, i) => {
              const prevWeek = i > 0 ? flatDays[i - 1].weekStartKey : null;
              const isWeekFirst = prevWeek !== d.weekStartKey;
              return (
                <TableHead
                  key={d.date}
                  className={`min-w-[90px] border-b border-gray-200 bg-gray-50 px-2 py-2 text-center text-xs font-semibold text-gray-700 ${
                    isWeekFirst ? "border-l-2 border-l-gray-300" : ""
                  }`}
                >
                  {d.label}
                </TableHead>
              );
            })}
            <TableHead className="min-w-[60px] border-b border-gray-200 bg-gray-50 px-2 py-2 text-right text-xs font-bold text-gray-800">
              Total
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.map(emp => {
            const total = getTotalForEmp(emp.id);
            return (
              <TableRow key={emp.id} className="bg-white">
                <TableCell className="sticky left-0 z-20 min-w-[140px] border-r border-gray-200 bg-white font-medium text-gray-900 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-600">
                      <Users className="h-4 w-4" />
                    </div>
                    {emp.name}
                  </div>
                </TableCell>
                {flatDays.map((d, i) => {
                  const prevWeek = i > 0 ? flatDays[i - 1].weekStartKey : null;
                  const isWeekFirst = prevWeek !== d.weekStartKey;
                  return renderDayCell(emp.id, d.date, isWeekFirst);
                })}
                <TableCell className="text-primary text-right font-semibold">
                  {total}h
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Sprint list expand: same card layout as “Select tasks for this sprint”, view-only; click task loads employee-sprint-task-hours */
function SprintTasksExpandContent({
  sprint,
  getProjectNameById,
}: {
  sprint: any;
  getProjectNameById: (
    id: string | number | null | undefined
  ) => { label: string } | null | undefined;
}) {
  const [viewOpenTaskId, setViewOpenTaskId] = useState<string | null>(null);
  const sprintId = String(sprint?.id ?? sprint?.ID ?? "");

  const { data: tasksData, isLoading } = useQuery({
    queryKey: ["sprint-expand-tasks", sprintId],
    queryFn: async () => {
      if (!sprintId) {
        return { tasks: [] };
      }
      const detail = await DynamicModuleService.getItemById(
        SPRINT_SCHEMA_NAME,
        sprintId
      );
      const list = detail?.tasks_list ?? [];
      const taskRefs = Array.isArray(list) ? list : [];
      const ids = taskRefs.map((x: any) => x?.task ?? x?.id).filter(Boolean);
      if (ids.length === 0) {
        return { tasks: [] };
      }
      const tasks = await Promise.all(
        ids.map((id: string) =>
          DynamicModuleService.getItemById(PTS_TASK_SCHEMA_NAME, String(id))
        )
      );
      return { tasks };
    },
    enabled: !!sprintId,
  });

  const { data: priorityOptions } = useQuery({
    queryKey: ["status-dropdown-sprint-expand", PTS_STATUS_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(PTS_STATUS_SCHEMA_NAME),
    enabled: !!tasksData?.tasks?.length,
  });

  const priorityIdToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    const raw = priorityOptions;
    const options = Array.isArray(raw)
      ? raw
      : (raw?.results ?? raw?.data ?? raw?.items ?? []);
    if (!Array.isArray(options)) {
      return map;
    }
    options.forEach((opt: any) => {
      const id = opt?.value ?? opt?.id ?? opt?.pk ?? "";
      const label =
        opt?.label ?? opt?.name ?? opt?.display_name ?? opt?.title ?? id;
      if (id !== null && String(id).trim() !== "") {
        const key = String(id);
        map[key] = String(label);
        map[key.toLowerCase()] = String(label);
      }
    });
    return map;
  }, [priorityOptions]);

  const getPriorityDisplayName = useCallback(
    (statusId: string | null | undefined) => {
      if (!statusId) {
        return "-";
      }
      const s = String(statusId).trim();
      return priorityIdToLabel[s] ?? priorityIdToLabel[s.toLowerCase()] ?? "-";
    },
    [priorityIdToLabel]
  );

  const priorityBadge = useCallback(
    (statusId: string | null | undefined) => {
      const label = getPriorityDisplayName(statusId);
      if (!label || label === "-") {
        return null;
      }
      const lower = label.toLowerCase();
      const isHigh = lower === "high";
      return (
        <Badge
          variant="outline"
          className={`text-[10px] ${isHigh ? "border-orange-300 bg-orange-100 text-orange-800" : ""}`}
        >
          {label}
        </Badge>
      );
    },
    [getPriorityDisplayName]
  );

  const { data: hoursRaw, isLoading: hoursLoading } = useQuery({
    queryKey: ["employee-sprint-task-hours-view", sprintId, viewOpenTaskId],
    queryFn: async () => {
      const raw = await get<EmployeeSprintTaskHoursResponse>(
        "/pts_app/resource_allocation/employee-sprint-task-hours/",
        {
          params: {
            task_id: viewOpenTaskId!,
            sprint_id: sprintId,
          },
        }
      );
      return raw;
    },
    enabled: !!sprintId && !!viewOpenTaskId,
  });

  const normalized = useMemo(() => {
    if (!hoursRaw || !viewOpenTaskId) {
      return null;
    }
    return normalizeSprintTaskHoursResponse(hoursRaw, viewOpenTaskId);
  }, [hoursRaw, viewOpenTaskId]);

  const viewDayHoursMap = useMemo(() => {
    if (!normalized?.__editSeedHours) {
      return {} as Record<string, Record<string, number>>;
    }
    const out: Record<string, Record<string, number>> = {};
    for (const [empId, dates] of Object.entries(normalized.__editSeedHours)) {
      out[empId] = { ...dates };
    }
    return out;
  }, [normalized]);

  const tasks = useMemo(() => tasksData?.tasks ?? [], [tasksData]);
  const projectId = sprint?.project ?? sprint?.project_id;
  const projectLabel = projectId
    ? (getProjectNameById(projectId)?.label ?? "-")
    : "-";

  const toggleViewTask = (taskId: string) => {
    setViewOpenTaskId(prev => (prev === taskId ? null : taskId));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (!tasks.length) {
    return (
      <div className="py-4 text-center text-sm text-gray-500">
        No tasks in this sprint.
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-2 overflow-hidden rounded-lg border border-gray-200 bg-[#fafafa] p-4">
      <h3 className="text-sm font-semibold text-[#111827]">Sprint tasks</h3>
      {/* <p className="text-[10px] text-muted-foreground">
        View only — click a task to load allocation (same layout as create
        sprint).
      </p> */}
      <div className="space-y-2">
        {tasks.map((task: any) => {
          const taskId = String(task?.id ?? task?.ID ?? "");
          const taskName =
            task?.taskname ?? task?.task_name ?? task?.name ?? "-";
          const estHours =
            task?.estimatedhours ??
            task?.taskestimatedhours ??
            task?.est_hours ??
            task?.estimated_hours ??
            0;
          const hoursDisplay =
            typeof estHours === "number" ? `${estHours}h` : `${estHours ?? 0}h`;
          const isOpen = viewOpenTaskId === taskId;
          const remainingData = isOpen ? normalized : null;
          const assignedEmployees = remainingData?.results ?? [];
          const firstWithWeeks = assignedEmployees.find(
            e => (e.week_wise?.length ?? 0) > 0
          );
          const weekWiseBlocks =
            firstWithWeeks?.week_wise?.filter(
              w => (w.remaining_hours?.length ?? 0) > 0
            ) ?? [];
          const hasWeekWise = weekWiseBlocks.length > 0;
          const sprintStart =
            sprint?.start_date ?? sprint?.startdate ?? sprint?.StartDate;
          const sprintEnd =
            sprint?.end_date ?? sprint?.enddate ?? sprint?.EndDate;
          const workingDays =
            sprintStart && sprintEnd
              ? getWorkingDays(dayjs(sprintStart), dayjs(sprintEnd))
              : [];

          return (
            <div
              key={taskId}
              className={`min-w-0 max-w-full overflow-hidden rounded-md border ${
                isOpen ? "border-primary/30" : "border-gray-200"
              } bg-white`}
            >
              <div
                className="bg-muted/20 flex cursor-pointer items-center gap-3 px-3 py-2"
                onClick={() => toggleViewTask(taskId)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    toggleViewTask(taskId);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <ListChecks className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-foreground text-xs font-semibold">
                  {taskName}
                </span>
                <Badge variant="outline" className="text-[10px]">
                  {hoursDisplay}
                </Badge>
                {priorityBadge(task?.status)}
                <span className="text-muted-foreground ml-1 text-[10px]">
                  {projectLabel}
                </span>
                {isOpen && assignedEmployees.length > 0 && (
                  <Badge variant="outline" className="ml-auto text-[10px]">
                    <Users className="mr-1 h-3 w-3" />
                    {assignedEmployees.length}
                  </Badge>
                )}
              </div>
              {isOpen && (
                <div className="min-w-0 px-4 py-2 pl-6">
                  {hoursLoading && (
                    <div className="flex justify-center py-4">
                      <LoadingSpinner />
                    </div>
                  )}
                  {!hoursLoading && hoursRaw && (
                    <>
                      {assignedEmployees.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2">
                          {assignedEmployees.map(r => (
                            <span
                              key={r.emp_id}
                              className="border-primary/30 bg-primary/10 flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs"
                            >
                              <span className="text-primary">✓</span>
                              {r.emp_name}
                            </span>
                          ))}
                        </div>
                      )}
                      {assignedEmployees.length > 0 && hasWeekWise && (
                        <WeekGroupedEmployeeGrid
                          readOnly
                          weekWiseBlocks={weekWiseBlocks}
                          employees={assignedEmployees.map(r => ({
                            id: r.emp_id,
                            name: r.emp_name,
                          }))}
                          dayHoursMap={viewDayHoursMap}
                          onUpdateHours={() => {}}
                          getRemainingHours={(empId, date) => {
                            const empResult = remainingData?.results?.find(
                              x => x.emp_id === empId
                            );
                            return getRemainingHoursForEmployee(
                              empResult,
                              date
                            );
                          }}
                          getDayAvailability={(empId, date) => {
                            const empResult = remainingData?.results?.find(
                              x => x.emp_id === empId
                            );
                            return getEmployeeDayAvailability(empResult, date);
                          }}
                        />
                      )}
                      {assignedEmployees.length > 0 &&
                        !hasWeekWise &&
                        workingDays.length > 0 && (
                          <DayWiseEmployeeGrid
                            readOnly
                            employees={assignedEmployees.map(r => ({
                              id: r.emp_id,
                              name: r.emp_name,
                            }))}
                            workingDays={workingDays}
                            dayHoursMap={viewDayHoursMap}
                            onUpdateHours={() => {}}
                            getRemainingHours={(empId, date) => {
                              const empResult = remainingData?.results?.find(
                                x => x.emp_id === empId
                              );
                              return getRemainingHoursForEmployee(
                                empResult,
                                date
                              );
                            }}
                            getDayAvailability={(empId, date) => {
                              const empResult = remainingData?.results?.find(
                                x => x.emp_id === empId
                              );
                              return getEmployeeDayAvailability(
                                empResult,
                                date
                              );
                            }}
                          />
                        )}
                      {assignedEmployees.length === 0 && (
                        <p className="text-muted-foreground py-2 text-[10px]">
                          No allocation rows for this task.
                        </p>
                      )}
                    </>
                  )}
                  {!hoursLoading && !hoursRaw && (
                    <p className="text-muted-foreground py-2 text-[10px]">
                      Could not load allocation.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SprintPage = () => {
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSprintId, setEditingSprintId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [createDayHours, setCreateDayHours] = useState<
    Record<string, Record<string, number>>
  >({});
  const createEditSectionRef = useRef<HTMLDivElement | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isEditSectionHighlighted, setIsEditSectionHighlighted] =
    useState(false);

  // Watch selected project and dates from form (for task list and employee-task-remaining)
  const selectedProjectForTasks = Form.useWatch("project", form);
  const startDateForm = Form.useWatch("start_date", form);
  const endDateForm = Form.useWatch("end_date", form);

  // Normalize dates from form (Ant Design may give dayjs, Date, or string)
  const toDayjs = (v: unknown): dayjs.Dayjs | null => {
    if (!v) {
      return null;
    }
    if (dayjs.isDayjs(v)) {
      return v as dayjs.Dayjs;
    }
    const d = dayjs(v as string | Date);
    return d.isValid() ? d : null;
  };
  const startDateDayjs = toDayjs(startDateForm);
  const endDateDayjs = toDayjs(endDateForm);

  // For API: use form dates, or fallback to today → today+6 so API is called when task is clicked even before dates are set
  const startDateStr =
    startDateDayjs?.format("DD-MM-YYYY") ?? dayjs().format("DD-MM-YYYY");
  const endDateStr =
    endDateDayjs?.format("DD-MM-YYYY") ??
    dayjs().add(6, "day").format("DD-MM-YYYY");

  // Fetch project dropdown for filter
  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  // Fetch project tasks when project is selected (for create/edit sprint task selection)
  const { data: projectTasksResponse, isLoading: projectTasksLoading } =
    useQuery({
      queryKey: ["project-task", selectedProjectForTasks, isEditMode],
      queryFn: async () => {
        if (!selectedProjectForTasks) {
          return { tasks: [] };
        }
        const params: { project: string; is_sprint?: boolean } = {
          project: String(selectedProjectForTasks),
        };
        if (!isEditMode) {
          params.is_sprint = true;
        }
        const response = await get<{
          success?: boolean;
          tasks?: any[];
        }>("/pts_app/project-task/", {
          params,
        });
        if (
          response?.success &&
          response?.tasks &&
          Array.isArray(response.tasks)
        ) {
          return { tasks: response.tasks };
        }
        if (Array.isArray(response)) {
          return { tasks: response };
        }
        return { tasks: [] };
      },
      enabled: !!selectedProjectForTasks,
    });

  const projectTasksList = useMemo(() => {
    const tasks = projectTasksResponse?.tasks ?? [];
    return Array.isArray(tasks) ? tasks : [];
  }, [projectTasksResponse]);

  // Fetch PTS Status dropdown to display priority/status name by id (task.status is the status id)
  const { data: priorityOptions } = useQuery({
    queryKey: ["status-dropdown", PTS_STATUS_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(PTS_STATUS_SCHEMA_NAME),
    enabled: !!selectedProjectForTasks,
  });

  const priorityIdToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    const raw = priorityOptions;
    const options = Array.isArray(raw)
      ? raw
      : (raw?.results ?? raw?.data ?? raw?.items ?? []);
    if (!Array.isArray(options)) {
      return map;
    }
    options.forEach((opt: any) => {
      const id = opt?.value ?? opt?.id ?? opt?.pk ?? "";
      const label =
        opt?.label ?? opt?.name ?? opt?.display_name ?? opt?.title ?? id;
      if (id !== null && String(id).trim() !== "") {
        const key = String(id);
        map[key] = String(label);
        map[key.toLowerCase()] = String(label); // case-insensitive fallback
      }
    });
    return map;
  }, [priorityOptions]);

  const getPriorityDisplayName = useCallback(
    (statusId: string | null | undefined) => {
      if (!statusId) {
        return "-";
      }
      const s = String(statusId).trim();
      return priorityIdToLabel[s] ?? priorityIdToLabel[s.toLowerCase()] ?? "-";
    },
    [priorityIdToLabel]
  );

  // Create mode: employee-task-remaining by sprint dates.
  // Edit mode: employee-sprint-task-hours by task_id + sprint_id.
  const remainingQueries = useQueries({
    queries: selectedTaskIds.map(taskId => ({
      queryKey: [
        isEditMode ? "employee-sprint-task-hours" : "employee-task-remaining",
        taskId,
        editingSprintId,
        startDateStr,
        endDateStr,
      ] as const,
      queryFn: async () => {
        if (isEditMode && editingSprintId) {
          const raw = await get<EmployeeSprintTaskHoursResponse>(
            "/pts_app/resource_allocation/employee-sprint-task-hours/",
            {
              params: {
                task_id: taskId,
                sprint_id: editingSprintId,
              },
            }
          );
          return normalizeSprintTaskHoursResponse(raw, taskId);
        }

        const res = await get<EmployeeTaskRemainingResponse>(
          "/pts_app/resource_allocation/employee-task-remaining/",
          {
            params: {
              task_id: taskId,
              sprint_start_date: startDateStr,
              sprint_end_date: endDateStr,
            },
          }
        );
        return res;
      },
      enabled: isEditMode
        ? !!taskId && !!editingSprintId
        : !!taskId && !!startDateStr && !!endDateStr,
    })),
  });

  // Map taskId -> API response for remaining hours per employee per day
  const employeeTaskRemainingMap = useMemo(() => {
    const map: Record<string, EmployeeTaskRemainingResponse> = {};
    selectedTaskIds.forEach((taskId, i) => {
      const data = remainingQueries[i]?.data;
      if (data?.results) {
        map[taskId] = data;
      }
    });
    return map;
  }, [selectedTaskIds, remainingQueries]);

  /** Seed allocation inputs from edit API (allocation_hours) once per sprint+task */
  const editAllocationSeededRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isEditMode || !editingSprintId) {
      editAllocationSeededRef.current.clear();
      return;
    }
    selectedTaskIds.forEach(taskId => {
      const data = employeeTaskRemainingMap[taskId];
      if (!data?.__editSeedHours) {
        return;
      }
      const seedKey = `${editingSprintId}:${taskId}`;
      if (editAllocationSeededRef.current.has(seedKey)) {
        return;
      }
      editAllocationSeededRef.current.add(seedKey);
      setCreateDayHours(prev => {
        const next = { ...prev };
        for (const [empId, dates] of Object.entries(data.__editSeedHours!)) {
          const k = `${taskId}-${empId}`;
          next[k] = { ...(next[k] ?? {}), ...dates };
        }
        return next;
      });
    });
  }, [isEditMode, editingSprintId, selectedTaskIds, employeeTaskRemainingMap]);

  // Get column grid data to generate all columns dynamically
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", SPRINT_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(SPRINT_SCHEMA_NAME),
  });

  // Fetch sprint data for editing
  const { data: sprintData, isLoading: sprintLoading } = useQuery({
    queryKey: ["sprint-data", SPRINT_SCHEMA_NAME, editingSprintId],
    queryFn: () => {
      if (!editingSprintId) {
        throw new Error("Sprint ID is required");
      }
      return DynamicModuleService.getItemById(
        SPRINT_SCHEMA_NAME,
        editingSprintId
      );
    },
    enabled: !!editingSprintId && isEditMode,
  });

  // Clear task selection when project changes; in edit mode only clear when user picks a different project (not on initial load when form project is still undefined)
  const sprintProjectId = sprintData?.project ?? sprintData?.project_id ?? null;
  useEffect(() => {
    if (!isEditMode) {
      setSelectedTaskIds([]);
    } else if (
      sprintProjectId !== null &&
      selectedProjectForTasks !== null &&
      String(selectedProjectForTasks) !== String(sprintProjectId)
    ) {
      setSelectedTaskIds([]);
    }
  }, [selectedProjectForTasks, isEditMode, sprintProjectId]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  // Helper function to get project name by ID
  const getProjectNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return null;
      }
      return projectOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
    },
    [projectOptions]
  );

  // Helper function to render status badge
  const renderStatusBadge = (status: string | undefined) => {
    if (!status) {
      return "-";
    }
    const statusLower = status.toLowerCase();

    if (statusLower === "active") {
      return <Tag color="orange">Active</Tag>;
    } else if (statusLower === "completed" || statusLower === "complete") {
      return <Tag color="green">Completed</Tag>;
    } else if (statusLower === "planned") {
      return <Tag color="default">Planned</Tag>;
    }
    return <Tag>{status}</Tag>;
  };

  // Format date helper
  const formatDate = (date: string | null | undefined) => {
    if (!date) {
      return "-";
    }
    try {
      const d = new Date(date);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return date;
    }
  };

  // Format date and time for edited tooltip.
  const formatDateTime = useCallback(
    (dateString: string | null | undefined) => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, "0");
        return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
      } catch {
        return dateString;
      }
    },
    []
  );

  // Toggle task selection
  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const isTaskSelected = (taskId: string) =>
    selectedTaskIds.includes(String(taskId));

  /** Priority badge for task card (e.g. High = orange) */
  const getPriorityBadge = useCallback(
    (statusId: string | null | undefined) => {
      const label = getPriorityDisplayName(statusId);
      if (!label || label === "-") {
        return null;
      }
      const lower = label.toLowerCase();
      const isHigh = lower === "high";
      return (
        <Badge
          variant="outline"
          className={`text-[10px] ${isHigh ? "border-orange-300 bg-orange-100 text-orange-800" : ""}`}
        >
          {label}
        </Badge>
      );
    },
    [getPriorityDisplayName]
  );

  // Populate form when sprint data is loaded for editing
  useEffect(() => {
    if (sprintData && isEditMode) {
      // Find field names from schema
      const findFieldName = (searchTerms: string[]) => {
        if (!columnGridData?.grid) {
          return null;
        }
        for (const field of columnGridData.grid) {
          const fieldName = field.field_name.toLowerCase();
          const fieldTitle = field.field_title?.toLowerCase() ?? "";
          if (
            searchTerms.some(
              term =>
                fieldName.includes(term.toLowerCase()) ??
                fieldTitle.includes(term.toLowerCase())
            )
          ) {
            return fieldName;
          }
        }
        return null;
      };

      const sprintNameField = findFieldName(["sprint_name", "name", "sprint"]);
      const projectField = findFieldName([
        "project",
        "project_id",
        "project_name",
      ]);
      const startDateField = findFieldName([
        "start_date",
        "startdate",
        "start",
      ]);
      const endDateField = findFieldName(["end_date", "enddate", "end"]);
      const statusField = findFieldName(["status", "sprint_status"]);

      const formValues: any = {};

      // Set sprint name
      if (sprintNameField && sprintData[sprintNameField]) {
        formValues.sprint_name = sprintData[sprintNameField];
      } else if (sprintData.sprint_name || sprintData.name) {
        formValues.sprint_name = sprintData.sprint_name ?? sprintData.name;
      }

      // Set project
      if (projectField && sprintData[projectField]) {
        formValues.project = sprintData[projectField];
      } else if (sprintData.project_id || sprintData.project) {
        formValues.project = sprintData.project_id ?? sprintData.project;
      }

      // Set start date (convert to dayjs)
      const startDate =
        (startDateField && sprintData[startDateField]) ??
        sprintData.start_date ??
        sprintData.startdate;
      if (startDate) {
        formValues.start_date = dayjs(startDate);
      }

      // Set end date (convert to dayjs)
      const endDate =
        (endDateField && sprintData[endDateField]) ??
        sprintData.end_date ??
        sprintData.enddate;
      if (endDate) {
        formValues.end_date = dayjs(endDate);
      }

      // Set status
      if (statusField && sprintData[statusField]) {
        formValues.status = sprintData[statusField];
      } else if (sprintData.status || sprintData.sprint_status) {
        formValues.status = sprintData.status ?? sprintData.sprint_status;
      }

      form.setFieldsValue(formValues);

      // Pre-fill selected tasks for edit: match tasks_list to project-task IDs (task ref may be in .task, .id, or nested)
      const rawTasks =
        sprintData.tasks_list ?? sprintData.tasks ?? sprintData.task_ids ?? [];
      const taskIds = Array.isArray(rawTasks)
        ? rawTasks
            .map((t: any) => {
              if (t === null) {
                return "";
              }
              const id =
                t?.task ??
                t?.id ??
                t?.ID ??
                (typeof t === "object" ? t?.task_id : t);
              return id !== null ? String(id) : "";
            })
            .filter(Boolean)
        : [];
      setSelectedTaskIds(taskIds);
    }
  }, [sprintData, isEditMode, form, columnGridData]);

  // Generate all columns dynamically
  const columns: ProColumns<any>[] = useMemo(() => {
    const handleDelete = async (id: string | undefined) => {
      if (!id) {
        notify.error("Sprint ID is missing");
        return;
      }
      try {
        const response = await DynamicModuleService.deleteItem(
          SPRINT_SCHEMA_NAME,
          String(id)
        );
        if (response) {
          queryClient.invalidateQueries({
            queryKey: ["moduleData", SPRINT_SCHEMA_NAME],
          });
        }
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete sprint");
      }
    };

    const handleEdit = (id: string | undefined) => {
      if (!id) {
        notify.error("Sprint ID is missing");
        return;
      }
      setEditingSprintId(String(id));
      setIsEditMode(true);
      setIsEditSectionHighlighted(true);
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = setTimeout(() => {
        setIsEditSectionHighlighted(false);
      }, 3000);
      // Scroll directly to create/edit section.
      createEditSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    if (!columnGridData?.grid) {
      return [];
    }

    // Find field names from schema
    const findFieldName = (searchTerms: string[]) => {
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return fieldName;
        }
      }
      return null;
    };

    const sprintNameField = findFieldName(["sprint_name", "name", "sprint"]);
    const projectField = findFieldName([
      "project",
      "project_id",
      "project_name",
    ]);
    const startDateField = findFieldName(["start_date", "startdate", "start"]);
    const endDateField = findFieldName(["end_date", "enddate", "end"]);
    // const tasksField = findFieldName(["tasks", "task_count", "total_tasks"]);
    const statusField = findFieldName(["status", "sprint_status"]);

    // Build custom columns matching the image design
    const customColumns: ProColumns<any>[] = [
      {
        title: "Sprint Name",
        dataIndex: sprintNameField ?? undefined,
        key: "sprint_name",
        render: (_: any, record: any) => {
          const sprintName =
            (sprintNameField && record[sprintNameField]) ??
            record.sprint_name ??
            record.name ??
            "-";
          const tasksList = record.tasks_list ?? record.tasks ?? [];
          const taskCount = Array.isArray(tasksList) ? tasksList.length : 0;

          const createdBy = record.created_by;
          const updatedBy = record.updated_by;
          const createdOn = record.created_on;
          const updatedOn = record.updated_on;
          const isEdited = createdBy !== updatedBy || createdOn !== updatedOn;
          const formattedDate = isEdited ? formatDateTime(updatedOn) : "";

          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{sprintName}</span>
                {taskCount > 0 && (
                  <span className="rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {taskCount} {taskCount === 1 ? "task" : "tasks"}
                  </span>
                )}
              </div>
              {isEdited && (
                <Tooltip
                  title={
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-800" />
                        <EmployeeTooltipContent userId={updatedBy} />
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-800" />
                        <span className="text-gray-700">{formattedDate}</span>
                      </div>
                    </div>
                  }
                  placement="bottom"
                  overlayInnerStyle={{
                    backgroundColor: "#ffffff",
                    color: "#374151",
                    borderRadius: "8px",
                    border: "1px solid #e0e6eb",
                    padding: "8px 12px",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  <div className="flex w-fit cursor-pointer items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                    <Pencil size={12} />
                    <span>Edited</span>
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Project",
        dataIndex: projectField ?? undefined,
        key: "project",
        render: (_: any, record: any) => {
          const projectId =
            (projectField && record[projectField]) ??
            record.project_id ??
            record.project;
          if (!projectId) {
            return "-";
          }
          const project = getProjectNameById(projectId);
          return project?.label ?? projectId;
        },
      },
      {
        title: "Start Date",
        dataIndex: startDateField ?? undefined,
        key: "start_date",
        render: (_: any, record: any) => {
          const startDate =
            (startDateField && record[startDateField]) ??
            record.start_date ??
            record.startdate;
          return formatDate(startDate);
        },
      },
      {
        title: "End Date",
        dataIndex: endDateField ?? undefined,
        key: "end_date",
        render: (_: any, record: any) => {
          const endDate =
            (endDateField && record[endDateField]) ??
            record.end_date ??
            record.enddate;
          return formatDate(endDate);
        },
      },
      // {
      //   title: "Tasks",
      //   dataIndex: tasksField ?? undefined,
      //   key: "tasks",
      //   render: (_: any, record: any) => {
      //     const tasks =
      //       (tasksField && record[tasksField]) ??
      //       record.tasks ??
      //       record.task_count ??
      //       record.total_tasks ??
      //       0;
      //     return typeof tasks === "number" ? tasks : (tasks ?? "0");
      //   },
      // },
      {
        title: "Status",
        dataIndex: statusField ?? undefined,
        key: "status",
        render: (_: any, record: any) => {
          const status =
            (statusField && record[statusField]) ??
            record.status ??
            record.sprint_status;
          return renderStatusBadge(status);
        },
      },
    ];

    return [
      ...customColumns,
      {
        title: "Actions",
        width: 120,
        valueType: "option",
        key: "actions",
        fixed: "right",
        render: (_: any, record: any) => {
          const recordId =
            record?.id ?? record?.ID ?? record?.Id ?? record?._id;

          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              onClick={() => {
                handleEdit(recordId);
              }}
              title="Edit Sprint"
            />,
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this sprint?"
              onConfirm={() => {
                handleDelete(recordId);
              }}
              okText="Yes"
              cancelText="No"
            >
              <CustomButton
                danger
                icon={<Trash size={16} />}
                type="text"
                title="Delete Sprint"
              />
            </Popconfirm>,
          ];
        },
      },
    ];
  }, [columnGridData, queryClient, getProjectNameById, formatDateTime]);

  // Build filter object
  const filter = useMemo(() => {
    const filterObj: any = {};
    if (searchQuery?.trim()) {
      const searchTerm = searchQuery.trim();
      filterObj.sprint_name__ilike = `%${searchTerm}%`;
    }
    if (projectFilter && projectFilter !== "all") {
      filterObj.project = projectFilter;
    }
    if (statusFilter && statusFilter !== "all") {
      filterObj.status = statusFilter;
    }
    return filterObj;
  }, [searchQuery, projectFilter, statusFilter]);

  // Handle form submission (both create and update)
  const handleSubmitSprint = async (values: any) => {
    try {
      setIsSubmitting(true);

      // Frontend guard for both create + update:
      // total allocated employee hours for a task must not exceed task estimated hours.
      for (const taskId of selectedTaskIds) {
        const task = projectTasksList.find(
          (t: any) => String(t?.id ?? t?.ID ?? "") === String(taskId)
        );
        const estimatedTaskHours = getTaskEstimatedHours(task);
        const apiTaskData = employeeTaskRemainingMap[taskId];
        const results = apiTaskData?.results ?? [];

        let totalAllocatedHours = 0;
        results.forEach(emp => {
          const byDate = createDayHours[`${taskId}-${emp.emp_id}`] ?? {};
          const dayEntries = emp.week_wise?.length
            ? emp.week_wise.flatMap(w => w.remaining_hours ?? [])
            : (emp.remaining_hours ?? []);

          dayEntries.forEach(({ date, hours: apiHours }) => {
            const { isLeave } = getEmployeeDayAvailability(emp, date);
            if (isLeave) {
              return;
            }
            const { effective } = getEffectiveAllocatedHours(
              byDate,
              date,
              apiHours
            );
            totalAllocatedHours += Number(effective);
          });
        });

        if (totalAllocatedHours > estimatedTaskHours) {
          throw new Error(
            "Total employee allocated hours cannot greater than task's estimated hours."
          );
        }
      }

      const now = new Date().toISOString();
      const tasks_list = selectedTaskIds.map((taskId, index) => ({
        task: taskId,
        created_on: now,
        index,
        updated_on: now,
      }));

      // Format dates to YYYY-MM-DD format; include selected tasks as tasks_list (array of { task, created_on, index, updated_on })
      const formattedValues = {
        ...values,
        start_date: values.start_date
          ? dayjs(values.start_date).format("YYYY-MM-DD")
          : null,
        end_date: values.end_date
          ? dayjs(values.end_date).format("YYYY-MM-DD")
          : null,
        tasks_list,
      };
      let sprintResponse: any;
      let sprintIdForAllocation: string | null = null;

      if (isEditMode && editingSprintId) {
        // Update existing sprint (Formily)
        sprintResponse = await DynamicModuleService.updateItem(
          SPRINT_SCHEMA_NAME,
          editingSprintId,
          formattedValues
        );
        sprintIdForAllocation = String(editingSprintId);
      } else {
        // Create new sprint (Formily)
        sprintResponse = await DynamicModuleService.createItem(
          SPRINT_SCHEMA_NAME,
          formattedValues
        );
        const createdId =
          sprintResponse?.id ??
          sprintResponse?.ID ??
          sprintResponse?.data?.id ??
          sprintResponse?.data?.ID;
        sprintIdForAllocation = createdId ? String(createdId) : null;
      }

      if (!sprintResponse) {
        throw new Error(`Failed to ${isEditMode ? "update" : "create"} sprint`);
      }

      if (!sprintIdForAllocation) {
        throw new Error(
          "Sprint saved, but could not resolve sprint id for employee allocation"
        );
      }

      // Build DRF payload from UI allocation matrix:
      // input value => allocated_hours, below-left value => remaining_hours
      const allocationTasks = selectedTaskIds.map(taskId => {
        const apiTaskData = employeeTaskRemainingMap[taskId];
        const emp_list: Array<{
          emp_id: string;
          date: string;
          allocated_hours: number;
          remaining_hours: number;
        }> = [];

        const results = apiTaskData?.results ?? [];
        results.forEach(emp => {
          const byDate = createDayHours[`${taskId}-${emp.emp_id}`] ?? {};
          const dayEntries = emp.week_wise?.length
            ? emp.week_wise.flatMap(w => w.remaining_hours ?? [])
            : (emp.remaining_hours ?? []);

          dayEntries.forEach(({ date, hours: apiHours }) => {
            const { isLeave } = getEmployeeDayAvailability(emp, date);
            const { effective } = getEffectiveAllocatedHours(
              byDate,
              date,
              apiHours
            );
            emp_list.push({
              emp_id: String(emp.emp_id),
              date,
              allocated_hours: isLeave ? 0 : Number(effective),
              remaining_hours: isLeave
                ? Number(apiHours)
                : Number(apiHours) - Number(effective),
            });
          });
        });

        return {
          task: taskId,
          emp_list,
        };
      });

      const allocationPayload = {
        sprint_id: sprintIdForAllocation,
        project: String(formattedValues.project ?? values.project ?? ""),
        tasks_list: allocationTasks,
      };

      const allocationResponse = await post(
        "/pts_app/resource_allocation/save-employee-allocation/",
        allocationPayload
      );

      if (!allocationResponse) {
        throw new Error("Failed to save employee allocation");
      }

      if (isEditMode) {
        handleCancelEdit();
        queryClient.invalidateQueries({
          queryKey: ["moduleData", SPRINT_SCHEMA_NAME],
        });
        queryClient.invalidateQueries({
          queryKey: ["sprint-data", SPRINT_SCHEMA_NAME],
        });
        queryClient.invalidateQueries({
          queryKey: ["sprint-expand-tasks"],
        });
      } else {
        form.resetFields();
        setSelectedTaskIds([]);
        setCreateDayHours({});
        queryClient.invalidateQueries({
          queryKey: ["moduleData", SPRINT_SCHEMA_NAME],
        });
      }
    } catch (error: any) {
      notify.error(
        error?.message ?? `Failed to ${isEditMode ? "update" : "create"} sprint`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingSprintId(null);
    setSelectedTaskIds([]);
    setCreateDayHours({});
    form.resetFields();
  };

  const toolBarRender = () => {
    return [
      <div key="search" className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by sprint name..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-64 pl-10"
        />
      </div>,
    ];
  };

  return (
    <div className="min-h-screen min-w-0 max-w-full space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Sprint Management
          </h1>
          <p className="text-sm text-[#627384]">
            Create and manage project sprints
          </p>
        </div>
      </div>

      {/* Create/Edit Sprint Section */}
      <div ref={createEditSectionRef}>
        <Card
          className={`min-w-0 max-w-full bg-white transition-all duration-300 ${
            isEditSectionHighlighted
              ? "border-primary/80 shadow-primary/30 border-2"
              : "border border-gray-200"
          }`}
        >
          <CardContent className="min-w-0 p-3 md:p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-[#111827]">
              {isEditMode ? (
                <>
                  <Edit2 className="h-5 w-5" />
                  Edit Sprint
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  Create New Sprint
                </>
              )}
            </h2>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmitSprint}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-5">
                <Form.Item
                  name="sprint_name"
                  label={
                    <span>
                      Sprint Name
                      {/* <span className="text-red-500">*</span> */}
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter sprint name" },
                  ]}
                  className="md:col-span-1"
                >
                  <Input
                    placeholder="e.g., Sprint 1"
                    className="h-[2rem] rounded-lg border-gray-200 bg-[#fafafa]"
                  />
                </Form.Item>

                <Form.Item
                  name="project"
                  label={
                    <span>
                      Project
                      {/* <span className="text-red-500">*</span> */}
                    </span>
                  }
                  rules={[{ required: true, message: "Please select project" }]}
                  className="md:col-span-1"
                >
                  <Select
                    allowClear
                    placeholder="Select project"
                    showSearch
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    className="rounded-lg"
                    options={projectOptions?.map(
                      (project: { value: string; label: string }) => ({
                        value: project.value,
                        label: project.label,
                      })
                    )}
                  />
                </Form.Item>

                <Form.Item
                  name="start_date"
                  label={
                    <span>
                      Start Date
                      {/* <span className="text-red-500">*</span> */}
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please select start date" },
                  ]}
                  className="md:col-span-1"
                >
                  <DatePicker
                    placeholder="dd-mm-yyyy"
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    className="rounded-lg"
                  />
                </Form.Item>

                <Form.Item
                  name="end_date"
                  label={
                    <span>
                      End Date
                      {/* <span className="text-red-500">*</span> */}
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please select end date" },
                  ]}
                  className="md:col-span-1"
                >
                  <DatePicker
                    placeholder="dd-mm-yyyy"
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    className="rounded-lg"
                  />
                </Form.Item>

                <Form.Item
                  name="status"
                  label={
                    <span>
                      Status
                      {/* <span className="text-red-500">*</span> */}
                    </span>
                  }
                  rules={[{ required: true, message: "Please select status" }]}
                  className="md:col-span-1"
                >
                  <Select
                    placeholder="Select status"
                    className="rounded-lg"
                    allowClear
                  >
                    <Select.Option value="active">Active</Select.Option>
                    <Select.Option value="completed">Completed</Select.Option>
                    <Select.Option value="planned">Planned</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              {/* Select tasks for this sprint - card design with employee grid and remaining hours */}
              {selectedProjectForTasks && (
                <div className="min-w-0 max-w-full space-y-2 rounded-lg border border-gray-200 bg-[#fafafa] p-4">
                  <h3 className="text-sm font-semibold text-[#111827]">
                    Select tasks for this sprint
                  </h3>
                  {projectTasksLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : projectTasksList.length === 0 ? (
                    <div className="text-muted-foreground rounded-md border border-gray-200 py-6 text-center text-xs">
                      {selectedProjectForTasks
                        ? `No tasks available for this project`
                        : "Select a project to see available tasks"}
                    </div>
                  ) : (
                    <div className="max-h-[420px] min-w-0 space-y-2 overflow-y-auto pr-1">
                      {projectTasksList.map((task: any) => {
                        const taskId = String(task?.id ?? task?.ID ?? "");
                        const taskName =
                          task?.taskname ??
                          task?.task_name ??
                          task?.name ??
                          "-";
                        const estHours =
                          task?.estimatedhours ??
                          task?.taskestimatedhours ??
                          task?.est_hours ??
                          task?.estimated_hours ??
                          0;
                        const hoursDisplay =
                          typeof estHours === "number"
                            ? `${estHours}h`
                            : `${estHours ?? 0}h`;
                        const isSelected = isTaskSelected(taskId);
                        const remainingData = employeeTaskRemainingMap[taskId];
                        const assignedEmployees = remainingData?.results ?? [];
                        const firstWithWeeks = assignedEmployees.find(
                          e => (e.week_wise?.length ?? 0) > 0
                        );
                        const weekWiseBlocks =
                          firstWithWeeks?.week_wise?.filter(
                            w => (w.remaining_hours?.length ?? 0) > 0
                          ) ?? [];
                        const hasWeekWise = weekWiseBlocks.length > 0;
                        const workingDays =
                          startDateDayjs && endDateDayjs
                            ? getWorkingDays(startDateDayjs, endDateDayjs)
                            : getWorkingDays(dayjs(), dayjs().add(6, "day"));

                        return (
                          <div
                            key={taskId}
                            className={`min-w-0 max-w-full overflow-hidden rounded-md border ${
                              isSelected
                                ? "border-primary/30"
                                : "border-gray-200"
                            } bg-white`}
                          >
                            {/* Task header */}
                            <div
                              className="bg-muted/20 flex cursor-pointer items-center gap-3 px-3 py-2"
                              onClick={() => toggleTaskSelection(taskId)}
                              onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                  toggleTaskSelection(taskId);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={isSelected}
                                onCheckedChange={() =>
                                  toggleTaskSelection(taskId)
                                }
                                onClick={e => e.stopPropagation()}
                              />
                              <ListChecks className="text-muted-foreground h-3.5 w-3.5" />
                              <span className="text-foreground text-xs font-semibold">
                                {taskName}
                              </span>
                              <Badge variant="outline" className="text-[10px]">
                                {hoursDisplay}
                              </Badge>
                              {getPriorityBadge(task?.status)}
                              {isSelected && assignedEmployees.length > 0 && (
                                <Badge
                                  variant="outline"
                                  className="ml-auto text-[10px]"
                                >
                                  <Users className="mr-1 h-3 w-3" />
                                  {assignedEmployees.length}
                                </Badge>
                              )}
                            </div>

                            {/* Employee chips + day-wise grid when task is selected */}
                            {isSelected && (
                              <div className="w-full overflow-hidden px-4 py-2 pl-6">
                                {!startDateDayjs || !endDateDayjs ? (
                                  <p className="text-muted-foreground py-1 text-[10px]">
                                    Using default date range. Set sprint start &
                                    end dates above for your sprint.
                                  </p>
                                ) : null}
                                {/* Employee chips from API response */}
                                {assignedEmployees.length > 0 && (
                                  <div className="mb-2 flex flex-wrap gap-2">
                                    {assignedEmployees.map(r => (
                                      <span
                                        key={r.emp_id}
                                        className="border-primary/30 bg-primary/10 flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs"
                                      >
                                        <span className="text-primary">✓</span>
                                        {r.emp_name}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Week-wise: one table, two-row header (week spans + days) */}
                                {assignedEmployees.length > 0 &&
                                  hasWeekWise && (
                                    <WeekGroupedEmployeeGrid
                                      weekWiseBlocks={weekWiseBlocks}
                                      employees={assignedEmployees.map(r => ({
                                        id: r.emp_id,
                                        name: r.emp_name,
                                      }))}
                                      dayHoursMap={Object.fromEntries(
                                        assignedEmployees.map(r => [
                                          r.emp_id,
                                          createDayHours[
                                            `${taskId}-${r.emp_id}`
                                          ] ?? {},
                                        ])
                                      )}
                                      onUpdateHours={(empId, date, hours) => {
                                        const key = `${taskId}-${empId}`;
                                        setCreateDayHours(prev => ({
                                          ...prev,
                                          [key]: {
                                            ...(prev[key] ?? {}),
                                            [date]: hours,
                                          },
                                        }));
                                      }}
                                      getRemainingHours={(empId, date) => {
                                        const empResult =
                                          remainingData?.results?.find(
                                            x => x.emp_id === empId
                                          );
                                        return getRemainingHoursForEmployee(
                                          empResult,
                                          date
                                        );
                                      }}
                                      getDayAvailability={(empId, date) => {
                                        const empResult =
                                          remainingData?.results?.find(
                                            x => x.emp_id === empId
                                          );
                                        return getEmployeeDayAvailability(
                                          empResult,
                                          date
                                        );
                                      }}
                                    />
                                  )}
                                {assignedEmployees.length > 0 &&
                                  !hasWeekWise &&
                                  workingDays.length > 0 && (
                                    <DayWiseEmployeeGrid
                                      employees={assignedEmployees.map(r => ({
                                        id: r.emp_id,
                                        name: r.emp_name,
                                      }))}
                                      workingDays={workingDays}
                                      dayHoursMap={Object.fromEntries(
                                        assignedEmployees.map(r => [
                                          r.emp_id,
                                          createDayHours[
                                            `${taskId}-${r.emp_id}`
                                          ] ?? {},
                                        ])
                                      )}
                                      onUpdateHours={(empId, date, hours) => {
                                        const key = `${taskId}-${empId}`;
                                        setCreateDayHours(prev => ({
                                          ...prev,
                                          [key]: {
                                            ...(prev[key] ?? {}),
                                            [date]: hours,
                                          },
                                        }));
                                      }}
                                      getRemainingHours={(empId, date) => {
                                        const empResult =
                                          remainingData?.results?.find(
                                            x => x.emp_id === empId
                                          );
                                        return getRemainingHoursForEmployee(
                                          empResult,
                                          date
                                        );
                                      }}
                                      getDayAvailability={(empId, date) => {
                                        const empResult =
                                          remainingData?.results?.find(
                                            x => x.emp_id === empId
                                          );
                                        return getEmployeeDayAvailability(
                                          empResult,
                                          date
                                        );
                                      }}
                                    />
                                  )}
                                {assignedEmployees.length === 0 &&
                                  remainingQueries.some(
                                    (q, i) =>
                                      selectedTaskIds[i] === taskId &&
                                      q.isLoading
                                  ) && (
                                    <div className="text-muted-foreground py-4 text-center text-xs">
                                      Loading remaining hours…
                                    </div>
                                  )}
                                {assignedEmployees.length === 0 &&
                                  !remainingQueries.find(
                                    (q, i) =>
                                      selectedTaskIds[i] === taskId &&
                                      q.isLoading
                                  ) &&
                                  remainingData !== undefined && (
                                    <p className="text-muted-foreground py-2 text-[10px]">
                                      No employee allocation data for this task
                                      in the selected date range.
                                    </p>
                                  )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                {isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting || sprintLoading}
                  className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                >
                  {isEditMode ? (
                    <>
                      <Edit2 className="h-4 w-4" />
                      {isSubmitting ? "Updating..." : "Update Sprint"}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {isSubmitting ? "Creating..." : "Create Sprint"}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* Sprint Table */}
      <Card className="min-w-0 max-w-full border border-gray-200 bg-white">
        <CardContent className="min-w-0 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 md:mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-[#111827]">
                Sprint List
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <Select
                value={projectFilter}
                onChange={setProjectFilter}
                className="w-40"
                placeholder="All Projects"
              >
                <Select.Option value="all">All Projects</Select.Option>
                {projectOptions?.map(
                  (project: { value: string; label: string }) => (
                    <Select.Option key={project.value} value={project.value}>
                      {project.label}
                    </Select.Option>
                  )
                )}
              </Select>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                className="w-32"
                placeholder="All Status"
              >
                <Select.Option value="all">All Status</Select.Option>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="completed">Completed</Select.Option>
                <Select.Option value="planned">Planned</Select.Option>
              </Select>
            </div>
          </div>
          <ConfigDynamicTable
            tableName={SPRINT_SCHEMA_NAME}
            columns={columns}
            headerTitle=""
            rowKey="id"
            filter={filter}
            toolBarRender={toolBarRender}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            expandable={{
              expandedRowRender: (record: any) => (
                <SprintTasksExpandContent
                  sprint={record}
                  getProjectNameById={getProjectNameById}
                />
              ),
              rowExpandable: (record: any) => {
                const list = record.tasks_list ?? record.tasks ?? [];
                return Array.isArray(list) && list.length > 0;
              },
              expandedRowClassName: () =>
                "[&_td.ant-table-cell]:!min-w-0 [&_td.ant-table-cell]:!max-w-full overflow-hidden",
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SprintPage;
